<template>
  <div
    class="cockpit-result-card"
    :class="{ 'cockpit-result-card--clickable': clickable }"
    :data-testid="testId"
    @click="onCardClick"
  >
    <div class="flex items-start justify-between mb-3">
      <p class="text-[10px] text-[#ABABAB] uppercase tracking-wider font-medium m-0">
        {{ label }}
      </p>
      <span
        v-if="ampelDot && !showPlaceholder"
        class="w-2.5 h-2.5 rounded-full shrink-0 mt-0.5"
        :style="{ backgroundColor: ampelDot }"
        aria-hidden="true"
      />
    </div>

    <p
      v-if="showPlaceholder"
      class="text-[13px] text-[#ABABAB] leading-tight py-2 m-0"
    >
      {{ $t('UserProjects.cockpit.insufficientSample') }}
    </p>

    <template v-else>
      <p
        v-if="value"
        class="text-[32px] font-bold text-[var(--color-dark)] leading-none mb-1.5 mt-0"
      >
        {{ value }}
      </p>
      <slot />
      <div
        v-if="hasDelta"
        class="flex items-center gap-1.5 mt-1"
      >
        <span class="text-[13px] text-[var(--color-disabled)]">
          {{ $t('UserProjects.cockpit.prevWeek', { value: prevValue }) }}
        </span>
        <span class="text-[13px] font-medium" :style="{ color: deltaColor }">
          {{ deltaValue }}
        </span>
        <i :class="deltaIconClass" :style="{ color: deltaColor }" aria-hidden="true" />
      </div>
      <p
        v-else-if="trend"
        class="text-[13px] mt-1 mb-0"
        :style="{ color: trendColor || '#6B6B6B' }"
      >
        {{ trend }}
      </p>
      <p
        v-if="hasNValue && nValue >= 5"
        class="text-xs text-[var(--color-disabled)] mt-0.5 mb-0"
      >
        n={{ nValue }} {{ nLabel }}
      </p>
      <p
        v-else-if="!hasNValue && sub"
        class="text-[12px] mt-0.5 mb-0"
        :style="{ color: subColor || '#ABABAB' }"
      >
        {{ sub }}
      </p>
    </template>
  </div>
</template>

<script>
export default {
  name: 'CockpitResultCard',
  props: {
    label: { type: String, required: true },
    value: { type: String, default: '' },
    ampelDot: { type: String, default: '' },
    trend: { type: String, default: '' },
    trendColor: { type: String, default: '' },
    sub: { type: String, default: '' },
    subColor: { type: String, default: '' },
    prevValue: { type: String, default: '' },
    deltaValue: { type: String, default: '' },
    nValue: { type: Number, default: null },
    nLabel: { type: String, default: '' },
    testId: { type: String, default: 'cockpit-result-card' },
    clickable: { type: Boolean, default: false },
  },
  emits: ['click'],
  computed: {
    hasNValue() {
      return this.nValue !== undefined && this.nValue !== null;
    },
    showPlaceholder() {
      return this.hasNValue && this.nValue < 5;
    },
    hasDelta() {
      return Boolean(this.prevValue) && Boolean(this.deltaValue);
    },
    deltaNum() {
      if (!this.deltaValue) return 0;
      return Number.parseInt(String(this.deltaValue).replace(/[^\d-]/g, ''), 10) || 0;
    },
    deltaColor() {
      if (this.deltaNum > 0) return '#22C55E';
      if (this.deltaNum < 0) return '#EF4444';
      return '#6B6B6B';
    },
    deltaIconClass() {
      if (this.deltaNum > 0) return 'pi pi-arrow-up text-[0.75rem]';
      if (this.deltaNum < 0) return 'pi pi-arrow-down text-[0.75rem]';
      return 'pi pi-arrow-right text-[0.75rem]';
    },
  },
  methods: {
    onCardClick() {
      if (this.clickable) this.$emit('click');
    },
  },
};
</script>

<style scoped>
.cockpit-result-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e5e5;
}

.cockpit-result-card--clickable {
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.cockpit-result-card--clickable:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
</style>
